import { Injectable } from '@angular/core';
import { poe1, poe2 } from '../../game';
import { Rarity, BaseTypeTier, JewelType } from '../models/enums';
import { Filter, JewelFilter, GearHighlightConfig } from '../models/filter-models';
import { ItemDataService } from './item-data.service';

@Injectable({
  providedIn: 'root'
})
export class PresetsHelperService {
  poe1 = poe1;
  poe2 = poe2;

  constructor(private itemDataService: ItemDataService) {
  }

  public initializeMyGearSettingsByProgress(filter: Filter) {
    let baseTypeTier = BaseTypeTier.Basic, rarity = Rarity.Normal, itemLevel,
      minTier: number | undefined, highlightConfig = new GearHighlightConfig();

    if (!filter.progressPreset) {
      // We don't know the progress preset; try to deduce it from already set exceptions
      const firstWeaponException = filter.weaponFilters[0];
      if (firstWeaponException) {
        baseTypeTier = firstWeaponException.baseTypeTier;
        rarity = firstWeaponException.rarity;
      } else {
        // No exceptions set yet; we can stick with the least strict ones
      }
    } else if (filter.progressPreset === 'presetEarlyCampaign1' || filter.progressPreset === 'presetMidCampaign30') {
      baseTypeTier = BaseTypeTier.Basic;
      rarity = Rarity.Normal;
    } else if (filter.progressPreset === 'presetLateCampaignEarlyMaps60') {
      baseTypeTier = BaseTypeTier.Advanced;
      rarity = Rarity.Normal;
    } else if (filter.progressPreset === 'presetMidMaps70') {
      baseTypeTier = BaseTypeTier.Expert;
      rarity = poe1 ? Rarity.Rare : Rarity.Normal;
    } else if (filter.progressPreset === 'presetEndGame80') {
      baseTypeTier = BaseTypeTier.Expert;
      rarity = Rarity.Magic;
      highlightConfig.regularNormal = false;
      highlightConfig.regularMagic = false;
      highlightConfig.regularRare = false;
      itemLevel = poe1 ? 83 : undefined;
      minTier = 2;
      highlightConfig.rareMinTier = 3;
      highlightConfig.magicMinTier = 2;
      highlightConfig.lowTierRareMinTier = 4;
      highlightConfig.lowTierMagicMinTier = 3;
    } else if (filter.progressPreset === 'presetStrict90') {
      baseTypeTier = BaseTypeTier.Elite;
      rarity = Rarity.Magic;
      highlightConfig.regularNormal = false;
      highlightConfig.regularMagic = false;
      highlightConfig.regularRare = false;
      itemLevel = poe1 ? 84 : undefined;
      minTier = 3;
      highlightConfig.rareMinTier = 4;
      highlightConfig.magicMinTier = 3;
      highlightConfig.lowTierRareMinTier = 5;
      highlightConfig.lowTierMagicMinTier = 4;
      highlightConfig.goodILevel = false;
    } else if (filter.progressPreset === 'presetVeryStrict95') {
      baseTypeTier = BaseTypeTier.Elite;
      rarity = Rarity.Magic;
      highlightConfig.regularNormal = false;
      highlightConfig.regularMagic = false;
      highlightConfig.regularRare = false;
      itemLevel = poe1 ? 84 : undefined;
      minTier = 5;
      highlightConfig.rare = false;
      highlightConfig.rareMinTier = 5;
      highlightConfig.magicMinTier = 5;
      highlightConfig.lowTierMagic = false;
      highlightConfig.lowTierRareMinTier = 5;
      highlightConfig.lowTierRare = false;
      highlightConfig.lowTierMagicMinTier = 5;
      highlightConfig.goodILevel = false;
      highlightConfig.excellentILevel = false;
    }

    if (baseTypeTier === BaseTypeTier.Basic) {
      highlightConfig.lowTierMagic = false;
      highlightConfig.lowTierRare = false;
    }

    if (poe2 && filter.ssf) {
      const reduceTier = (tier: number, minTier?: number) => Math.max(minTier ?? 2, tier - 1);
      minTier = undefined;
      highlightConfig.rareMinTier = reduceTier(highlightConfig.rareMinTier);
      highlightConfig.magicMinTier = reduceTier(highlightConfig.magicMinTier);
      highlightConfig.lowTierRareMinTier = reduceTier(highlightConfig.lowTierRareMinTier, 3);
      highlightConfig.lowTierMagicMinTier = reduceTier(highlightConfig.lowTierMagicMinTier, 3);

      if (filter.progressPreset === 'presetVeryStrict95') {
        rarity = Rarity.Magic;
        highlightConfig.regularMagic = true;
      } else {
        rarity = Rarity.Normal;
        highlightConfig.regularMagic = true;
        highlightConfig.regularNormal = true;
      }
    }

    return { baseTypeTier, rarity, itemLevel, minTier, highlightConfig };
  }

  setPoe2JewelFilters(filter: Filter) {
    const classPreset = filter.classPreset;
    const progressPreset = filter.progressPreset;

    if (!classPreset || classPreset === 'None' || classPreset === 'Scion') return;

    let jewelTypes: JewelType[] = [];
    switch (classPreset) {
      case 'Warrior': jewelTypes = [JewelType.Ruby]; break;
      case 'Ranger': jewelTypes = [JewelType.Emerald]; break;
      case 'Huntress': jewelTypes = [JewelType.Emerald]; break;
      case 'Druid': jewelTypes = [JewelType.Sapphire, JewelType.Ruby]; break;
      case 'Witch': jewelTypes = [JewelType.Sapphire]; break;
      case 'Sorceress': jewelTypes = [JewelType.Sapphire]; break;
      case 'Mercenary': jewelTypes = [JewelType.Ruby, JewelType.Emerald]; break;
      case 'Monk': jewelTypes = [JewelType.Emerald, JewelType.Sapphire]; break;
    }

    const newJewelFilter = new JewelFilter({
      fromClassPreset: true,
      show: true,
      jewelType: jewelTypes,
      rarity: Rarity.Magic,
    });

    if (progressPreset === 'presetEndGame80' || progressPreset === 'presetStrict90') {
      newJewelFilter.rarity = Rarity.Rare;
      newJewelFilter.highlightConfig.regularMagic = false;
    } else if (progressPreset === 'presetVeryStrict95') {
      newJewelFilter.show = false;
    }

    filter.jewelFilters = filter.jewelFilters.filter(j => !j.fromClassPreset && !j.fromProgressPreset);
    filter.jewelFilters.push(newJewelFilter);
  }
}
